/**
 * Escopo de connection_id para ler/atualizar chat_conversations.
 * Instagram nunca herda a conexão WhatsApp do seletor global.
 */
import { jidsMatch } from '../../../utils/phoneUtils';
import type { ChatConversation } from '../hooks/types';

export function resolveConversationConnectionScope(
    remoteJid: string,
    options: {
        conversationConnectionId?: string | null;
        selectedConnectionId?: string | null;
    },
): string | null {
    if (remoteJid.startsWith('ig:')) {
        return options.conversationConnectionId ?? null;
    }
    return options.conversationConnectionId ?? options.selectedConnectionId ?? null;
}

export function scopeConversationQuery<T extends { eq: (col: string, val: string) => T }>(
    query: T,
    connectionId: string | null,
    organizationId?: string,
): T {
    let next = query;
    if (connectionId) next = next.eq('connection_id', connectionId);
    if (organizationId) next = next.eq('organization_id', organizationId);
    return next;
}

/**
 * Busca scoped vazia + connection pedida → tentar linha legado sem connection_id.
 * Org é obrigatória para não vazar tenant.
 */
export function shouldQueryOrphanConversation(
    hasMatch: boolean,
    connectionId?: string | null,
    organizationId?: string | null,
): boolean {
    return !hasMatch && Boolean(connectionId) && Boolean(organizationId);
}

/**
 * Resolve a linha correta (WA-A vs WA-B vs IG) pelo mesmo telefone/JID.
 * Preferência: connectionId exato → órfã (null) → única match.
 */
export function findConversation(
    conversations: ChatConversation[],
    remoteJid: string,
    connectionId?: string | null,
): ChatConversation | undefined {
    const matches = conversations.filter((c) => jidsMatch(c.id, remoteJid));
    if (!matches.length) return undefined;
    if (connectionId) {
        return matches.find((c) => c.connectionId === connectionId)
            ?? matches.find((c) => !c.connectionId);
    }
    if (matches.length === 1) return matches[0];
    return matches.find((c) => c.connectionId) ?? matches[0];
}

/** Patch local: mesmo JID; connection irmã fica de fora; órfã (null) aceita. */
export function conversationIdentityMatch(
    c: ChatConversation,
    remoteJid: string,
    connectionId?: string | null,
): boolean {
    if (!jidsMatch(c.id, remoteJid)) return false;
    if (connectionId && c.connectionId && c.connectionId !== connectionId) return false;
    return true;
}
